import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  TerminateEmployeeDocument,
  TerminatePageEmployeeFragment,
  TerminatePageTerminationTypeFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getEmployeeJobRole } from 'modules/job_catalog/lib/utils'

import FirstStep from './FirstStep'

interface Props {
  employee: TerminatePageEmployeeFragment
  terminationTypes: TerminatePageTerminationTypeFragment[]
  canTerminateEmployees: boolean
  onClose: () => void
  onSuccess: () => void
}

const TerminationModal: React.FC<Props> = ({
  employee,
  terminationTypes,
  canTerminateEmployees,
  onClose,
  onSuccess,
}) => {
  const [terminateEmployee] = useMutation(TerminateEmployeeDocument, {
    extractorFn: (data) => data.employeesMutations.terminateEmployeesEmployee,
  })
  const [isConfirming, setIsConfirming] = React.useState<boolean>(false)

  const initialManagerId: number | undefined = employee.manager?.id

  const terminateFormValues = {
    pre_warning: true,
    manager_id: initialManagerId,
  }

  const form = useLazyRef(
    () =>
      new Form(terminateFormValues, {
        terminated_on: 'date',
        contract_end_date: 'date',
        termination_type_id: 'number',
        termination_reason_type: 'string',
        termination_reason: 'string',
        termination_observations: 'string',
        manager_id: 'number',
        stop_pending_approvals: 'boolean',
      })
  ).current

  const isTerminationDateValid = (): boolean => {
    const date = form.get('terminated_on').value

    return date !== '' && stringToDate(date, 'ui').isValid()
  }

  const fullName = employee.fullName
  const employeeRole = getEmployeeJobRole(employee.referenceContract)
  const terminationDate = form.data().terminated_on

  const contractEndDate = form.data().contract_end_date

  const isSubmitDisabled = !isTerminationDateValid() || !form.data().termination_reason_type

  const onClickTerminate = () => {
    setIsConfirming(true)
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!isConfirming || !canTerminateEmployees) return
    const data = form.data()
    const result = await terminateEmployee({
      variables: {
        id: employee.id,
        contractEndDate: data.contract_end_date,
        stopPendingApprovals: data.stop_pending_approvals === true ? true : false,
        terminatedOn: terminationDate,
        terminationAssignedManagerId: data.manager_id,
        terminationReason: data.termination_reason,
        terminationReasonType: data.termination_reason_type,
        terminationTypeId: data.termination_type_id,
      },
    })

    const handled = handleResult(result, (errors) => {
      const isEffectiveOnError = 'effectiveOn' in errors
      const message = () => {
        if (isEffectiveOnError) {
          return i18n.t('employees.termination.contract_version_effective_on_error')
        }
        if ('message' in errors) {
          return errors['message']
        }
        if ('messages' in errors) {
          return errors['messages'] || i18n.t('employees.termination.default_error')
        }
        return i18n.t('employees.termination.default_error')
      }

      app.setFlash({
        type: 'wrong',
        message: message(),
        time: isEffectiveOnError ? 4000 : 2000,
      })
      setIsConfirming(false)
    })
    if (handled.success) {
      closePortal()
      onSuccess()
    }

    const terminationReason = form.data().termination_type_id
      ? terminationTypes.find((type) => type.id === form.data().termination_type_id)?.slug || '-'
      : form.data().termination_reason_type

    track(trackingEvents.employee.termination.terminateSubmit, {
      company_id: employee.legalEntity?.company.id ?? 0,
      user_id: employee.id,
      user_role: employeeRole,
      time_stamp: Date.now(),
      fields_edited: form.dirtyFieldsKeys,
      reason_of_termination: terminationReason,
      is_successful: handled.success,
      error_code: !handled.success && JSON.stringify(handled.errors[0]),
      contract_end_date_used: !!contractEndDate,
    })
  }

  const onCancel = () => {
    if (!isConfirming) {
      track(trackingEvents.employee.termination.terminateCancel, {
        company_id: employee.legalEntity?.company.id ?? 0,
        user_id: employee.id,
        user_role: employeeRole,
        time_stamp: Date.now(),
        fields_edited: form.dirtyFieldsKeys,
      })
    }
    onClose()
  }

  const onBackToFirstStep = () => {
    setIsConfirming(false)
  }

  const description = (): string | React.ReactElement => {
    const today = moment().format('YYYY-MM-DD')
    if (isConfirming) {
      if (terminationDate < today) {
        return i18n.t('employees.termination.modal_description_past_date', {
          date: form.get('terminated_on').value,
        })
      }

      const baseDescription = i18n.t('employees.termination.modal_description_future_date')

      if (contractEndDate && terminationDate === contractEndDate) {
        const additionalMessage = i18n.t('employees.termination.same_date_description', {
          name: fullName,
          date: terminationDate,
        })
        return (
          <Box gap="s20">
            <Text.Normal>{additionalMessage}</Text.Normal>
            <Text.Normal>{baseDescription}</Text.Normal>
          </Box>
        )
      }

      return baseDescription
    } else {
      return i18n.t('employees.termination.modal_description')
    }
  }

  return (
    <Modal
      illustration="user-remove"
      title={i18n.t('employees.termination.action', { name: fullName })}
      description={description()}
      onSubmit={onSubmit}
      onClose={() => onCancel()}
      footer={
        <>
          {!isConfirming && (
            <Button onClick={onClickTerminate} type="negative" disabled={isSubmitDisabled}>
              {i18n.t('employees.termination.action', {
                name: fullName,
              })}
            </Button>
          )}
          {isConfirming && (
            <>
              <Button onClick={onBackToFirstStep} type="secondary">
                {i18n.t('common.back')}
              </Button>
              <Button submit type="negative">
                {i18n.t('employees.termination.action', {
                  name: fullName,
                })}
              </Button>
            </>
          )}
        </>
      }
    >
      {!isConfirming && (
        <FirstStep employee={employee} form={form} terminationTypes={terminationTypes} />
      )}
    </Modal>
  )
}

export default observer(TerminationModal)
